import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { fetchCronJobs, triggerCronJob, fetchEmailLogs, goodJobDashboardUrl } from "api";
import { PageHeader, Table, Spinner, EmptyState, Modal } from "components/common";
import type { EmailLog, EmailLogStatus } from "types";

const CRON_LABELS: Record<string, string> = {
  send_weekly_user_schedule:       "Weekly courier schedule",
  send_weekly_partner_schedule:    "Weekly partner schedule",
  send_daily_appointment_reminder: "Daily appointment reminder",
};

const PER_PAGE = 20;

export default function MailerToolsPage() {
  const navigate    = useNavigate();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<"" | EmailLogStatus>("");
  const [page, setPage]                 = useState(1);
  const [triggeredKey, setTriggeredKey]  = useState<string | null>(null);
  const [selectedError, setSelectedError] = useState<EmailLog | null>(null);

  const { data: cronJobs = [], isLoading: cronLoading } = useQuery({
    queryKey: ["mailerTools", "cronJobs"],
    queryFn:  fetchCronJobs,
  });

  const { data: logsResponse, isLoading: logsLoading } = useQuery({
    queryKey: ["mailerTools", "emailLogs", statusFilter, page],
    queryFn:  () => fetchEmailLogs({ page, per_page: PER_PAGE, status: statusFilter || undefined }),
  });

  const trigger = useMutation({
    mutationFn: (key: string) => triggerCronJob(key),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ["mailerTools", "cronJobs"] }),
  });

  const logs = logsResponse?.email_logs ?? [];
  const meta = logsResponse?.meta;

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8">
      <button
        onClick={() => navigate("/tools")}
        className="text-sm text-gray-400 hover:text-gray-600 mb-6 flex items-center gap-1 transition-colors"
      >
        ← Back to Tools
      </button>

      <PageHeader
        title="Mailer tools"
        subtitle="Notification-layer cron jobs, the background job dashboard, and email delivery history"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Cron jobs tile */}
        <div className="card p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">Scheduled (cron) jobs</h2>

          {cronLoading ? (
            <Spinner />
          ) : (
            <div className="space-y-3">
              {cronJobs.map((job) => (
                <div
                  key={job.key}
                  className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-800">{CRON_LABELS[job.key] ?? job.key}</p>
                    <p className="text-xs text-gray-400 mt-0.5 flex items-center flex-wrap gap-1">
                      {job.last_run_at ? (
                        <>
                          Last run: {format(parseISO(job.last_run_at), "MMM d, yyyy h:mm a")}
                          <MailerStatusBadge status={job.last_status} />
                        </>
                      ) : (
                        "Never run"
                      )}
                    </p>
                    {job.last_status === "error" && job.last_error && (
                      <p className="text-xs text-red-500 mt-0.5 truncate" title={job.last_error}>
                        {job.last_error}
                      </p>
                    )}
                  </div>
                  <button
                    className="btn-secondary shrink-0 text-xs"
                    disabled={trigger.isPending && triggeredKey === job.key}
                    onClick={() => { setTriggeredKey(job.key); trigger.mutate(job.key); }}
                  >
                    {trigger.isPending && triggeredKey === job.key ? "Queuing…" : "Trigger now"}
                  </button>
                </div>
              ))}
            </div>
          )}

          {trigger.isSuccess && (
            <p className="text-xs text-green-700 mt-3">
              Job queued — check the GoodJob dashboard, or refresh in a moment, to see it complete.
            </p>
          )}
          {trigger.isError && (
            <p className="text-xs text-red-600 mt-3">
              {(trigger.error as Error)?.message ?? "Failed to queue job"}
            </p>
          )}
        </div>

        {/* GoodJob dashboard tile */}
        <div className="card p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">GoodJob dashboard</h2>
          <p className="text-xs text-gray-500 mb-4">
            Opens GoodJob&rsquo;s built-in job/cron dashboard in a new tab, using your current admin session.
          </p>
          <button
            className="btn-primary w-full"
            onClick={() => window.open(goodJobDashboardUrl(), "_blank", "noopener,noreferrer")}
          >
            Launch GoodJob dashboard
          </button>
        </div>
      </div>

      {/* Email log history */}
      <div className="card">
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <h2 className="text-sm font-semibold text-gray-800">Email log history</h2>
          <select
            className="border border-gray-200 rounded-lg px-2 py-1 text-xs"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as "" | EmailLogStatus); setPage(1); }}
          >
            <option value="">All statuses</option>
            <option value="success">Success</option>
            <option value="error">Error</option>
          </select>
        </div>

        {logsLoading ? (
          <Spinner className="py-10" />
        ) : logs.length === 0 ? (
          <EmptyState message="No email log entries yet." />
        ) : (
          <>
            <Table<EmailLog>
              data={logs}
              keyExtractor={(l) => l.id}
              columns={[
                {
                  header:   "Date",
                  accessor: (l) => format(parseISO(l.created_at), "MMM d, yyyy h:mm a"),
                },
                {
                  header:   "Mail key",
                  accessor: (l) => <span className="font-mono text-xs">{l.mailer_key}</span>,
                },
                {
                  header:   "Status",
                  accessor: (l) => <MailerStatusBadge status={l.status} />,
                },
                {
                  header:   "Queued at",
                  accessor: (l) => (l.queued_at ? format(parseISO(l.queued_at), "MMM d, h:mm a") : "—"),
                },
                {
                  header:   "Sent at",
                  accessor: (l) => (l.sent_at ? format(parseISO(l.sent_at), "MMM d, h:mm a") : "—"),
                },
                {
                  header:   "Recipients",
                  accessor: (l) => (
                    <span className="text-xs text-gray-500 truncate block max-w-[220px]" title={l.user_ids}>
                      {l.user_ids}
                    </span>
                  ),
                },
                {
                  header:   "Error",
                  accessor: (l) =>
                    l.error ? (
                      <div className="max-w-[240px]">
                        <span className="text-xs text-red-600 truncate block" title={l.error}>
                          {l.error}
                        </span>
                        <button
                          className="text-xs text-brand-600 hover:text-brand-700 hover:underline transition-colors"
                          onClick={(e) => { e.stopPropagation(); setSelectedError(l); }}
                        >
                          View full error
                        </button>
                      </div>
                    ) : (
                      "—"
                    ),
                },
              ]}
            />

            {selectedError && (
              <Modal
                title={`Error — ${selectedError.mailer_key}`}
                onClose={() => setSelectedError(null)}
              >
                <div className="space-y-3 text-xs">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-gray-600">
                    <div>
                      <span className="font-medium text-gray-800">Logged:</span>{" "}
                      {format(parseISO(selectedError.created_at), "MMM d, yyyy h:mm a")}
                    </div>
                    <div>
                      <span className="font-medium text-gray-800">Queued at:</span>{" "}
                      {selectedError.queued_at ? format(parseISO(selectedError.queued_at), "MMM d, yyyy h:mm a") : "—"}
                    </div>
                    <div className="col-span-2 break-all">
                      <span className="font-medium text-gray-800">Recipients:</span>{" "}
                      {selectedError.user_ids || "—"}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg px-3 py-2 max-h-[50vh] overflow-y-auto">
                    <p className="font-mono text-red-700 whitespace-pre-wrap break-all">
                      {selectedError.error}
                    </p>
                  </div>
                </div>
              </Modal>
            )}

            {meta && meta.total_pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 text-xs text-gray-500">
                <span>
                  Page {meta.page} of {meta.total_pages} ({meta.total_count} total)
                </span>
                <div className="flex gap-2">
                  <button
                    className="btn-secondary text-xs"
                    disabled={meta.page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </button>
                  <button
                    className="btn-secondary text-xs"
                    disabled={meta.page >= meta.total_pages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function MailerStatusBadge({ status }: { status: string | null }) {
  if (!status) return null;

  const styles: Record<string, string> = {
    success: "bg-green-100 text-green-700",
    error:   "bg-red-100 text-red-700",
    running: "bg-amber-100 text-amber-700",
  };

  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${styles[status] ?? "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
}
